/**
 * StrategyComparisonCard — side-by-side comparison of every deal strategy
 * relevant to the current property. Sits in the Offers tab under the
 * offer log so operators can pivot exit strategies without leaving the
 * command center.
 *
 * Behaviors:
 *   • Auto-runs `calc_all` on mount so infeasible strategies still render
 *     (with a "needs X input" chip). Nothing hides silently.
 *   • Inline input row per strategy for the user_inputs each one needs.
 *     Submitting refreshes just that row's calc — no full reload.
 *   • "Pin as active" button on each row → PATCH /strategy → refresh.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Calculator, Check, Pin, TrendingUp, AlertTriangle, Sparkles,
  FileText, Copy, X,
} from "lucide-react";

// Human-friendly labels for every possible user_input across all strategies.
const INPUT_META = {
  cosmetic_cost:          { label: "Cosmetic Cost",       prefix: "$",  step: 100 },
  listed_price:           { label: "Listed Price",        prefix: "$",  step: 1000 },
  assignment_fee:         { label: "Assignment Fee",      prefix: "$",  step: 500 },
  existing_loan_balance:  { label: "Loan Balance",        prefix: "$",  step: 1000 },
  cash_to_seller:         { label: "Cash to Seller",      prefix: "$",  step: 500 },
  reserves:               { label: "Reserves",            prefix: "$",  step: 500 },
  monthly_rent:           { label: "Monthly Rent",        prefix: "$",  step: 50 },
  offer_price:            { label: "Offer Price",         prefix: "$",  step: 1000 },
  interest_rate_pct:      { label: "Rate %",              suffix: "%",  step: 0.25 },
  amortization_years:     { label: "Amort. Years",        suffix: "yr", step: 1 },
  down_payment:           { label: "Down Payment",        prefix: "$",  step: 500 },
  noi_annual:             { label: "NOI (yearly)",        prefix: "$",  step: 500 },
  target_cap_rate_pct:    { label: "Target Cap %",        suffix: "%",  step: 0.5 },
  comp_price_per_acre:    { label: "$/Acre (Comp)",       prefix: "$",  step: 100 },
  entitlement_cost:       { label: "Entitlement Cost",    prefix: "$",  step: 500 },
  dev_margin_pct:         { label: "Dev Margin %",        suffix: "%",  step: 5 },
};

function fmtMoney(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString()}`;
}

export default function StrategyComparisonCard({ property }) {
  const propertyId = property?.id;
  const dealType = (property?.deal_type || "house").toLowerCase();

  const [rows, setRows] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inputs, setInputs] = useState({});     // { strategy_key: { field: value } }
  const [busy, setBusy] = useState(null);       // strategy_key currently saving/recalcing
  const [templateModal, setTemplateModal] = useState(null); // {strategy_key, markdown, label} or null
  const [generatingKey, setGeneratingKey] = useState(null);

  const flatInputs = useMemo(() => {
    // Merge every strategy's user inputs into a single flat dict for the
    // POST body (the engine dedupes what each strategy needs).
    const out = {};
    for (const stratKey of Object.keys(inputs)) {
      Object.assign(out, inputs[stratKey] || {});
    }
    return out;
  }, [inputs]);

  const load = useCallback(async (mergeInputs) => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${propertyId}/strategies`,
        { inputs: mergeInputs ?? flatInputs },
      );
      setRows(data.results || []);
      setActiveKey(data.active_strategy_key || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Strategy calc failed");
    } finally {
      setLoading(false);
    }
  }, [propertyId, flatInputs]);

  useEffect(() => { load({}); /* initial run with no inputs */ }, [propertyId]);

  const setField = (stratKey, field, value) => {
    setInputs((s) => ({
      ...s,
      [stratKey]: { ...(s[stratKey] || {}), [field]: value },
    }));
  };

  const recalc = async (stratKey) => {
    setBusy(stratKey);
    try {
      await load();
    } finally {
      setBusy(null);
    }
  };

  const pin = async (stratKey) => {
    setBusy(stratKey);
    try {
      const { data } = await api.patch(
        `/v3/properties/${propertyId}/strategy`,
        { key: stratKey, inputs: inputs[stratKey] || {} },
      );
      setActiveKey(data.active_strategy_key);
      toast.success(`Pinned: ${data.active_strategy.label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't pin strategy");
    } finally {
      setBusy(null);
    }
  };

  const generateTemplate = async (stratKey, stratLabel) => {
    setGeneratingKey(stratKey);
    try {
      const { data } = await api.post(
        `/v3/properties/${propertyId}/offer-template`,
        { inputs: { strategy_key: stratKey, ...(inputs[stratKey] || {}) } },
      );
      setTemplateModal({
        strategy_key: stratKey,
        strategy_label: stratLabel,
        markdown: data.markdown,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't generate template");
    } finally {
      setGeneratingKey(null);
    }
  };

  const copyTemplate = async () => {
    if (!templateModal?.markdown) return;
    try {
      await navigator.clipboard.writeText(templateModal.markdown);
      toast.success("Template copied to clipboard");
    } catch {
      toast.error("Couldn't copy — select and copy manually");
    }
  };

  const downloadTemplate = () => {
    if (!templateModal?.markdown) return;
    const blob = new Blob([templateModal.markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `PA-${templateModal.strategy_key}-${(property?.address?.street || "property")
      .toLowerCase().replace(/[^a-z0-9]+/g, "-")}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!propertyId) return null;

  return (
    <section
      className="rounded-xl border border-amber-200 bg-white shadow-sm overflow-hidden"
      data-testid="strategy-comparison-card"
    >
      <header className="px-4 py-3 border-b border-amber-100 bg-gradient-to-r from-amber-50/70 to-white flex items-center gap-2 flex-wrap">
        <div className="w-8 h-8 rounded-lg grid place-items-center bg-amber-500/10 text-amber-700 shrink-0">
          <Calculator size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black text-amber-800">
            <Sparkles size={10} /> Deal Strategy Engine
          </div>
          <h3 className="font-display font-bold text-[15px] tracking-tight text-slate-900 leading-tight">
            Every exit strategy, side-by-side
          </h3>
        </div>
        <div className="text-[10.5px] uppercase tracking-widest font-black text-slate-500 shrink-0">
          {dealType} deal
        </div>
      </header>

      {loading ? (
        <div className="p-6 text-sm text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Running the math…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-sm text-slate-500">
          No strategies apply to this deal type yet.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="strategy-rows">
          {rows.map((r) => {
            const isActive = r.key === activeKey;
            const feasible = !!r.feasible;
            const needsInputs = r.needs_inputs || [];
            const currentInputs = inputs[r.key] || {};
            return (
              <li
                key={r.key}
                data-testid={`strategy-row-${r.key}`}
                className={`px-4 py-3 ${isActive ? "bg-amber-50/50" : ""}`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-bold text-[13.5px] text-slate-900">
                        {r.label}
                      </div>
                      {isActive && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-[9.5px] font-black uppercase tracking-widest px-1.5 py-0.5">
                          <Check className="w-2.5 h-2.5" /> Active
                        </span>
                      )}
                      {!feasible && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-[9.5px] font-black uppercase tracking-widest px-1.5 py-0.5">
                          <AlertTriangle className="w-2.5 h-2.5" /> Needs input
                        </span>
                      )}
                    </div>

                    {/* Numbers row */}
                    <div className="flex items-center gap-4 mt-1 flex-wrap">
                      <div>
                        <div className="text-[9.5px] uppercase tracking-widest font-black text-slate-500">Max Offer</div>
                        <div
                          className={`text-[15px] font-black tabular-nums ${
                            feasible ? "text-emerald-700" : "text-slate-400"
                          }`}
                          data-testid={`strategy-mao-${r.key}`}
                        >
                          {fmtMoney(r.max_offer)}
                        </div>
                      </div>
                      {r.monthly_cashflow !== null && r.monthly_cashflow !== undefined && (
                        <div>
                          <div className="text-[9.5px] uppercase tracking-widest font-black text-slate-500 inline-flex items-center gap-1">
                            <TrendingUp className="w-2.5 h-2.5" /> Monthly Flow
                          </div>
                          <div className="text-[15px] font-black tabular-nums text-slate-900">
                            {fmtMoney(r.monthly_cashflow)}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Formula narrative */}
                    <div className="text-[11.5px] text-slate-600 mt-1 leading-snug">
                      {r.notes}
                    </div>
                  </div>

                  {/* Pin + Generate PA buttons */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => generateTemplate(r.key, r.label)}
                      disabled={generatingKey === r.key || !feasible}
                      data-testid={`strategy-template-${r.key}`}
                      title="Generate a strategy-specific Purchase Agreement template"
                      className="inline-flex items-center gap-1 rounded-md text-[11px] font-bold px-2 py-1 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-40"
                    >
                      {generatingKey === r.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                      PA
                    </button>
                    <button
                      type="button"
                      onClick={() => pin(r.key)}
                      disabled={busy === r.key || !feasible || isActive}
                      data-testid={`strategy-pin-${r.key}`}
                      className={`inline-flex items-center gap-1 rounded-md text-[11px] font-bold px-2.5 py-1 ${
                        isActive
                          ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                          : "text-white shadow-md hover:opacity-90"
                      } disabled:opacity-50`}
                      style={isActive ? {} : { backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
                    >
                      {busy === r.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pin className="w-3 h-3" />}
                      {isActive ? "Active" : "Pin"}
                    </button>
                  </div>
                </div>

                {/* User inputs for this strategy (if any) */}
                {r.user_inputs && r.user_inputs.length > 0 && (
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {r.user_inputs.map((field) => {
                      const meta = INPUT_META[field] || { label: field };
                      const missing = needsInputs.includes(field);
                      return (
                        <label key={field} className="block">
                          <div className={`text-[9.5px] uppercase tracking-widest font-black mb-0.5 ${
                            missing ? "text-rose-700" : "text-slate-500"
                          }`}>
                            {meta.label}{missing ? " *" : ""}
                          </div>
                          <div className="relative">
                            {meta.prefix && (
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">
                                {meta.prefix}
                              </span>
                            )}
                            <input
                              type="number"
                              step={meta.step}
                              value={currentInputs[field] ?? ""}
                              onChange={(e) => setField(r.key, field, e.target.value)}
                              onBlur={() => recalc(r.key)}
                              data-testid={`strategy-input-${r.key}-${field}`}
                              className={`w-full rounded-md border px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-amber-400 ${
                                missing ? "border-rose-300" : "border-slate-200"
                              } ${meta.prefix ? "pl-5" : ""} ${meta.suffix ? "pr-6" : ""}`}
                            />
                            {meta.suffix && (
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">
                                {meta.suffix}
                              </span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {templateModal && (
        <div
          className="fixed inset-0 z-[9999] bg-slate-900/50 flex items-center justify-center p-4"
          data-testid="offer-template-modal"
          onClick={() => setTemplateModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 bg-gradient-to-r from-amber-50 to-white">
              <FileText className="w-4 h-4 text-amber-700" />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest font-black text-amber-800">
                  Purchase Agreement · Draft
                </div>
                <div className="font-display font-bold text-[15px] text-slate-900 leading-tight">
                  {templateModal.strategy_label}
                </div>
              </div>
              <button
                type="button"
                onClick={copyTemplate}
                data-testid="template-copy-btn"
                className="inline-flex items-center gap-1 rounded-md text-[11px] font-bold px-2.5 py-1 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
              <button
                type="button"
                onClick={downloadTemplate}
                data-testid="template-download-btn"
                className="inline-flex items-center gap-1 rounded-md text-[11px] font-bold px-2.5 py-1 text-white shadow-md hover:opacity-90"
                style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
              >
                <FileText className="w-3 h-3" /> Download .md
              </button>
              <button
                type="button"
                onClick={() => setTemplateModal(null)}
                data-testid="template-close-btn"
                className="rounded-md p-1 hover:bg-slate-100 text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="overflow-y-auto flex-1 px-6 py-5">
              <pre className="whitespace-pre-wrap text-[12.5px] leading-[1.55] font-mono text-slate-800 selection:bg-amber-200">
{templateModal.markdown}
              </pre>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
